import React, {
  ReactElement,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ReactComponent as LogoSvg } from '@assets/icons/logo.svg';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import MediaViewer from '@pages/MediaViewer';
import {
  ApprovalPasswordProvider,
  useApprovalPassword
} from '@context/ApprovalPasswordContext';
import LinkProtected from '@pages/ExternalReviewer/LinkProtected';
import AccessClose from '@pages/ExternalReviewer/AccessClose';
import ApprovalReviewerMediaLayout from '@layouts/ApprovalReviewerMediaLayout';
import { useAuth } from '@hooks/useAuth';
import EnterEmail from '@pages/ExternalReviewer/EnterEmail/EnterEmail';
import UploadAssetModal from '@components/Modals/UploadAssetModal';
import ApprovalModal from '@components/Modals/ApprovalModal/ApprovalModal';
import { WebSocketClientProvider } from '@hooks/useWebSocketClient';
import '@pages/ExternalReviewer/ExternalReviewer.less';
import UploadingAssetsProcess from '@components/UploadingAssetsProcess';
import { AssetsUppyProvider } from '@context/AssetsUppyContext';
import {
  OrganizationProvider,
  useOrganization
} from '@components/OrganizationBoundary';
import { useFetch } from '@hooks/useFetch';
import {
  organizationItemsListStateSelector,
  organizationListStateSelector
} from '@redux/selectors/organizations';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { fetchOrganizationsList } from '@redux/actions/organizations';
import { useTypedSelector } from '@hooks';
import ApprovalAssetsList from '@components/Approval/ApprovalAssetsList';
import SelectFilesModal from '@components/Modals/SelectFilesModal';
import { apiClient } from '@api/client';
import { setWorkspaces } from '@redux/actions/workspaceAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import TaskModal from '@components/Tasks/TaskModal';
import { TaskAttachmentsUppyProvider } from '@context/TaskAttachmentsUppyContext';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';
import OnboardingProcessProvider from '@context/OnboardingProcessProvider';

const WorkspaceCreationModal = React.lazy(
  () => import('@components/Modals/WorkspaceCreationModal')
);
const ApprovalAssetModal = React.lazy(
  () => import('@components/Modals/ApprovalModal/ApprovalAssetModal')
);
interface ApprovalReviewerProps {
  element: ReactElement;
}

export function ApprovalReviewerPage({ element }: ApprovalReviewerProps) {
  return (
    <div className="external_reviewer_container">
      <div className="external_reviewer_top">
        <LogoSvg />
      </div>
      <div className="external_reviewer_content">{element}</div>
      <div className="external_reviewer_bottom">
        New to StreamWork? Create your <a href="/login">own account</a> and
        start collaborating.
      </div>
    </div>
  );
}

interface ApprovalReviewerContainerProps {
  loader: ReactElement;
}

function ApprovalReviewerContainer({ loader }: ApprovalReviewerContainerProps) {
  const { password, setPassword } = useApprovalPassword();
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, error } = useAuth();
  const { currentOrganization } = useOrganization(false);
  const assetId = searchParams.get('assetId');
  const awId = searchParams.get('id');
  const isDisabledByParam = searchParams.get('active') === 'false';

  const awQuery = useGetApprovalWorkflow({
    params: { id: awId ?? '', password },
    enabled: !!awId && !isDisabledByParam && !!user
  });

  const errorCode = isAxiosError(awQuery.error)
    ? (awQuery.error.response?.data as any)?.errorCode
    : null;

  const passwordRequired = errorCode === 'password_does_not_match';
  const passwordDoesNotMatch = errorCode === 'password_does_not_match';
  const isDisabled =
    isDisabledByParam || !awId || (awQuery.isError && !passwordRequired);
  const { isLoading } = awQuery;

  const approvalWorkflow = awQuery.data?.approvalWorkflow ?? null;
  const commentMentions = awQuery.data?.commentMentions ?? false;

  // Single-asset auto-navigate
  useEffect(() => {
    if (!approvalWorkflow) return;
    const versionId = searchParams.get('versionId');
    const forceBack = searchParams.get('forceBack');
    if (!assetId && approvalWorkflow.assets?.length === 1 && !forceBack) {
      const firstAsset = approvalWorkflow.assets[0];
      navigate(
        `/approval?id=${approvalWorkflow.id}&assetId=${
          firstAsset.id
        }&versionId=${versionId ?? firstAsset.versionId}`
      );
    }
  }, [approvalWorkflow, assetId, navigate, searchParams]);

  const isOrgMember =
    !!currentOrganization?.entity?.id &&
    currentOrganization.entity.id === approvalWorkflow?.organizationId;
  useEffect(() => {
    if (!isOrgMember) return;
    (async () => {
      await dispatch(setWorkspaces());
    })();
  }, [isOrgMember, dispatch]);
  const [_, setCurrentWorkspace] = useCurrentWorkspace(false);
  const [workspaceResolved, setWorkspaceResolved] = useState(true);
  const workspaces = useTypedSelector((state) => state.workspace.workspaceList);
  const approvalWorkspace = workspaces.find(
    (x) => x.id === approvalWorkflow?.workspaceId
  );
  useEffect(() => {
    if (!approvalWorkspace?.id) {
      setWorkspaceResolved(true);
      return;
    }
    setWorkspaceResolved(false);
    setCurrentWorkspace(approvalWorkspace.id).finally(() =>
      setWorkspaceResolved(true)
    );
  }, [approvalWorkspace?.id, setCurrentWorkspace]);

  const beforeViewPage = useMemo<'login' | 'password' | null>(() => {
    if (!user || error?.status === 404) return 'login';
    if (passwordRequired && passwordDoesNotMatch) return 'password';
    return null;
  }, [user, error?.status, passwordRequired, passwordDoesNotMatch]);

  if (isDisabled || error?.error === 'not_aw_reviewer') {
    return (
      <ApprovalReviewerPage
        element={<AccessClose type="approval" awReason="not-a-reviewer" />}
      />
    );
  }
  if (beforeViewPage === 'login') {
    return <ApprovalReviewerPage element={<EnterEmail />} />;
  }
  if (beforeViewPage === 'password') {
    return (
      <ApprovalReviewerPage
        element={
          <LinkProtected
            password={password}
            passwordDoesNotMatch={passwordDoesNotMatch}
            setPassword={setPassword}
            loading={isLoading}
          />
        }
      />
    );
  }

  if (isLoading) {
    return loader;
  }

  if (!approvalWorkflow) {
    // This should not happen
    return (
      <ApprovalReviewerPage
        element={<AccessClose type="approval" awReason="not-a-reviewer" />}
      />
    );
  }

  if (!workspaceResolved) {
    return loader;
  }

  return (
    <TaskAttachmentsUppyProvider>
      <AssetsUppyProvider>
        <Routes>
          <Route
            path="*"
            element={
              <ApprovalReviewerMediaLayout
                layoutType={assetId ? 'asset' : 'batch-assets-list'}
                approvalWorkflow={approvalWorkflow}
                isOrgMember={isOrgMember}
              />
            }
          >
            <Route
              index
              element={
                assetId ? (
                  <MediaViewer
                    commentMentions={commentMentions}
                    isLoadingAsset={isLoading}
                  />
                ) : (
                  <ApprovalAssetsList
                    password={password}
                    awId={awId}
                    onOpenAsset={({ approval, asset }) => {
                      navigate(
                        `/approval?id=${approval.id}&assetId=${asset.id}&versionId=${asset.versionId}`
                      );
                    }}
                    initialData={{ approvalWorkflow, commentMentions }}
                  />
                )
              }
            />
          </Route>
        </Routes>
        <TaskModal />
        <ApprovalModal />
        <UploadAssetModal />
        <UploadingAssetsProcess />
        <SelectFilesModal />
        {isOrgMember && (
          <Suspense fallback={null}>
            <WorkspaceCreationModal />
          </Suspense>
        )}
        <Suspense fallback={null}>
          <ApprovalAssetModal />
        </Suspense>
      </AssetsUppyProvider>
    </TaskAttachmentsUppyProvider>
  );
}

export default function ApprovalReviewer(
  props: ApprovalReviewerContainerProps
) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const initialLoader = useRef(isLoading);
  if (!isLoading) initialLoader.current = false;

  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewIdParam = searchParams.get('reviewId');
  const batchIdParam = searchParams.get('batchId');
  const commentIdParam = searchParams.get('commentId');

  const organizationsLoading = useFetch({
    disabled: user?.type !== 'internal' || !user.emailVerified,
    key: `organizations`,
    selector: (state) => organizationListStateSelector(state).fetch,
    fetch: (fetchType) => dispatch(fetchOrganizationsList({ fetchType }))
  });
  const organizations = useTypedSelector((state) =>
    organizationItemsListStateSelector(state)
  );

  useEffect(() => {
    if (!reviewIdParam) return;
    // Old-style approval URL
    // Redirect to the new URL format
    (async () => {
      try {
        const { data } =
          await apiClient.approvalWorkflow.approvalWorkflowControllerGetNewAwLink(
            {
              reviewId: reviewIdParam,
              batchId: batchIdParam,
              commentId: commentIdParam
            }
          );
        window.location.href = data.link;
      } catch {
        navigate('/error-404');
      }
    })();
  }, [batchIdParam, commentIdParam, reviewIdParam, navigate]);

  const showLoader =
    reviewIdParam ||
    (!isAuthenticated && initialLoader.current) ||
    organizationsLoading;

  if (showLoader) return props.loader;

  return (
    <OnboardingProcessProvider>
      <WebSocketClientProvider>
        <OrganizationProvider organizations={organizations}>
          <ApprovalPasswordProvider>
            <ApprovalReviewerContainer {...props} />
          </ApprovalPasswordProvider>
        </OrganizationProvider>
      </WebSocketClientProvider>
    </OnboardingProcessProvider>
  );
}
